/**
 * 格局／用神／喜忌 — 公開子平結構匹配（標籤表）
 * 只做可驗證結構條件；唔產生長文論斷；唔抄韋氏原文。
 */
import type { BaziResult, ShiShen, Wuxing } from './bazi';
import { CANGGAN, STEM_WUXING, SHENG, KE, shiShenOf } from './bazi';
import gejuTable from '../../rules/geju.json';

export interface DetectNotes {
  月令_本氣十神?: string[];
  透干_十神?: string[];
  月支?: string[];
  從旺?: boolean;
  從弱?: boolean;
  從兒?: boolean;
  從財?: boolean;
  從殺?: boolean;
  日主五行?: Wuxing[];
  可見五行_最少?: Partial<Record<Wuxing, number>>;
  可見五行_佔比_最少?: number;
  主導五行?: Wuxing[];
  flags?: string[];
  match_mode?: 'any' | 'all';
  priority?: number;
  candidate_only?: boolean;
}

export interface GejuRule {
  id: string;
  name: string;
  kind?: 'yueling' | 'cong' | 'tiaohe' | 'placeholder';
  detect_notes: DetectNotes;
  用神_tags: string[];
  喜: string[];
  忌: string[];
  source_note: string;
}

export interface GejuTable {
  version: string;
  source_policy: string;
  rules: GejuRule[];
}

export type MatchStatus = 'matched' | 'candidate' | 'ambiguous';

export interface GejuMatch {
  rule: GejuRule;
  status: MatchStatus;
  score: number;
  reasons: string[];
}

export interface StructuralSnap {
  dayMaster: string;
  dayWuxing: Wuxing;
  monthZhi: string;
  yueLingBenQi: string;
  yueLingShiShen: ShiShen;
  touGanShiShen: ShiShen[];
  visible: Record<Wuxing, number>;
  dominant: Wuxing | null;
  dominantRatio: number;
  dayMasterCount: number;
  /** 日主生出（食傷五行）可見數 */
  shiShangCount: number;
  /** 日主所剋（財星五行）可見數 */
  caiCount: number;
  /** 剋日主（官殺五行）可見數 */
  guanShaCount: number;
  /** 生日主（印星五行）可見數 */
  yinCount: number;
  flags: string[];
}

export interface GejuMatchResult {
  matches: GejuMatch[];
  /** 最高分且非僅佔位；多個同分 → ambiguous */
  primary: GejuMatch[];
  structural: StructuralSnap;
}

/** Internal structural flags → Traditional Chinese UI labels (never show raw keys). */
export const FLAG_LABELS: Record<string, string> = {
  element_abundant: '五行偏旺',
  metal_abundant: '金偏旺',
  cong_wang_structural: '從旺結構',
  cong_ruo_structural: '從弱結構',
  cong_er_structural: '從兒結構',
  cong_cai_structural: '從財結構',
  cong_sha_structural: '從殺結構',
  daymaster_weak_placeholder: '日主偏弱（佔位）',
  jingang_placeholder: '金剛類（佔位）',
};

/** Map one flag key to ZH; unknown keys return null (do not leak English ids). */
export function flagLabel(flag: string): string | null {
  return FLAG_LABELS[flag] ?? null;
}

/** Join flags as Traditional Chinese for UI meta line. */
export function formatFlagsZh(flags: string[]): string {
  const labels = flags.map((f) => FLAG_LABELS[f]).filter((x): x is string => !!x);
  return labels.length ? labels.join('、') : '—';
}

const table = gejuTable as GejuTable;

export function loadGejuTable(): GejuTable {
  return table;
}

export function validateGejuTable(t: GejuTable = table): string[] {
  const errs: string[] = [];
  if (!t.version) errs.push('missing version');
  if (!Array.isArray(t.rules)) {
    errs.push('rules must be array');
    return errs;
  }
  const ids = new Set<string>();
  for (const r of t.rules) {
    if (!r.id) errs.push('rule missing id');
    else if (ids.has(r.id)) errs.push(`duplicate id ${r.id}`);
    else ids.add(r.id);
    if (!r.name) errs.push(`${r.id}: missing name`);
    if (!r.detect_notes || typeof r.detect_notes !== 'object') {
      errs.push(`${r.id}: missing detect_notes`);
    }
    if (!Array.isArray(r.用神_tags)) errs.push(`${r.id}: 用神_tags`);
    if (!Array.isArray(r.喜)) errs.push(`${r.id}: 喜`);
    if (!Array.isArray(r.忌)) errs.push(`${r.id}: 忌`);
    if (typeof r.source_note !== 'string') errs.push(`${r.id}: source_note`);
  }
  return errs;
}

/** 月支本氣（藏干第一位） */
export function yueLingBenQi(monthZhi: string): string {
  return (CANGGAN[monthZhi] ?? [])[0] ?? '';
}

function emptyWx(): Record<Wuxing, number> {
  return { 木: 0, 火: 0, 土: 0, 金: 0, 水: 0 };
}

/** 剋日主的五行（官殺五行） */
function guanShaWx(day: Wuxing): Wuxing {
  // KE[x]===day → x 剋 day
  const keys = Object.keys(KE) as Wuxing[];
  return keys.find((k) => KE[k] === day) ?? day;
}

/** 生日主的五行（印星五行） */
function yinWx(day: Wuxing): Wuxing {
  const keys = Object.keys(SHENG) as Wuxing[];
  return keys.find((k) => SHENG[k] === day) ?? day;
}

/**
 * 結構快照：月令十神、可見五行、從旺／從弱／從兒／從財／從殺旗。
 *
 * 從格旗為公開子平常用結構門檻（可見數／佔比），僅作候選，唔作定論。
 * - 從旺：日主同行偏旺（≥4 且佔比≥50%）
 * - 從弱：日主同行偏少（≤1）且無從旺，且食傷／財／官殺任一≥3
 * - 從兒：食傷五行主導且≥3、日主同行≤1、印少
 * - 從財：財星五行主導且≥3、日主同行≤1
 * - 從殺：官殺五行主導且≥3、日主同行≤1
 */
export function structuralSnapshot(r: BaziResult): StructuralSnap {
  const dayGan = r.dayMaster;
  const dayWuxing = STEM_WUXING[dayGan];
  const month = r.pillars.find((p) => p.label === '月柱')!;
  const monthZhi = month.zhi;
  const ben = yueLingBenQi(monthZhi);
  const yueLingShiShen = shiShenOf(dayGan, ben) as ShiShen;
  const touGanShiShen: ShiShen[] = r.pillars
    .filter((p) => p.label !== '日柱')
    .map((p) => p.ganShiShen);

  const visible = { ...r.wuxing.visible };
  const total = Object.values(visible).reduce((a, b) => a + b, 0) || 1;
  let dominant: Wuxing | null = null;
  let dominantCount = 0;
  (Object.keys(visible) as Wuxing[]).forEach((k) => {
    if (visible[k] > dominantCount) {
      dominant = k;
      dominantCount = visible[k];
    }
  });
  const dominantRatio = dominantCount / total;

  const shiShangWx = SHENG[dayWuxing];
  const caiWx = KE[dayWuxing];
  const guanWx = guanShaWx(dayWuxing);
  const yinW = yinWx(dayWuxing);

  const dayMasterCount = visible[dayWuxing] ?? 0;
  const shiShangCount = visible[shiShangWx] ?? 0;
  const caiCount = visible[caiWx] ?? 0;
  const guanShaCount = visible[guanWx] ?? 0;
  const yinCount = visible[yinW] ?? 0;

  const flags: string[] = [];
  if (dominant && dominantRatio >= 0.5 && dominantCount >= 4) {
    flags.push('element_abundant');
    if (dominant === '金') flags.push('metal_abundant');
    if (dayWuxing === dominant) flags.push('cong_wang_structural');
  }

  // 從弱／從兒／從財／從殺：日主偏弱結構門檻（與從旺互斥）
  const weakRoot = dayMasterCount <= 1 && !flags.includes('cong_wang_structural');
  if (weakRoot && (shiShangCount >= 3 || caiCount >= 3 || guanShaCount >= 3)) {
    flags.push('cong_ruo_structural');
    flags.push('daymaster_weak_placeholder');
  }
  if (
    weakRoot &&
    shiShangCount >= 3 &&
    dominant === shiShangWx &&
    yinCount <= 1
  ) {
    flags.push('cong_er_structural');
  }
  if (weakRoot && caiCount >= 3 && dominant === caiWx) {
    flags.push('cong_cai_structural');
  }
  if (weakRoot && guanShaCount >= 3 && dominant === guanWx) {
    flags.push('cong_sha_structural');
  }

  return {
    dayMaster: dayGan,
    dayWuxing,
    monthZhi,
    yueLingBenQi: ben,
    yueLingShiShen,
    touGanShiShen,
    visible,
    dominant,
    dominantRatio,
    dayMasterCount,
    shiShangCount,
    caiCount,
    guanShaCount,
    yinCount,
    flags,
  };
}

function ruleDisabled(rule: GejuRule): boolean {
  // 金剛佔位：無啟用條件則永不自動命中
  if (rule.id === 'jingang_placeholder') return true;
  return false;
}

function matchRule(
  rule: GejuRule,
  snap: StructuralSnap,
): GejuMatch | null {
  if (ruleDisabled(rule)) return null;
  if (
    rule.detect_notes.flags?.includes('jingang_placeholder') &&
    !snap.flags.includes('jingang_placeholder')
  ) {
    return null;
  }

  const d = rule.detect_notes;
  const reasons: string[] = [];
  const checks: boolean[] = [];

  if (d.月令_本氣十神?.length) {
    const ok = d.月令_本氣十神.includes(snap.yueLingShiShen);
    checks.push(ok);
    if (ok) reasons.push(`月令本氣${snap.yueLingBenQi}→${snap.yueLingShiShen}`);
  }

  if (d.透干_十神?.length) {
    const ok = d.透干_十神.some((s) => snap.touGanShiShen.includes(s as ShiShen));
    checks.push(ok);
    if (ok) {
      reasons.push(
        `透干含${d.透干_十神.filter((s) => snap.touGanShiShen.includes(s as ShiShen)).join('/')}`,
      );
    }
  }

  if (d.月支?.length) {
    const ok = d.月支.includes(snap.monthZhi);
    checks.push(ok);
    if (ok) reasons.push(`月支${snap.monthZhi}`);
  }

  if (d.日主五行?.length) {
    const ok = d.日主五行.includes(snap.dayWuxing);
    checks.push(ok);
    if (ok) reasons.push(`日主五行${snap.dayWuxing}`);
  }

  if (d.主導五行?.length) {
    const ok = !!snap.dominant && d.主導五行.includes(snap.dominant);
    checks.push(ok);
    if (ok) reasons.push(`主導${snap.dominant}`);
  }

  if (d.可見五行_最少) {
    let ok = true;
    for (const [k, min] of Object.entries(d.可見五行_最少)) {
      if ((snap.visible[k as Wuxing] ?? 0) < (min as number)) ok = false;
    }
    checks.push(ok);
    if (ok) {
      const parts = Object.entries(d.可見五行_最少)
        .map(([k, min]) => `${k}≥${min}(實${snap.visible[k as Wuxing]})`)
        .join(',');
      reasons.push(parts);
    }
  }

  if (d.可見五行_佔比_最少 != null && d.主導五行?.length) {
    const ok =
      snap.dominant != null &&
      d.主導五行.includes(snap.dominant) &&
      snap.dominantRatio >= d.可見五行_佔比_最少;
    checks.push(ok);
    if (ok) {
      reasons.push(
        `佔比${(snap.dominantRatio * 100).toFixed(0)}%≥${(d.可見五行_佔比_最少 * 100).toFixed(0)}%`,
      );
    }
  } else if (d.可見五行_佔比_最少 != null && !d.主導五行?.length) {
    const ok = snap.dominantRatio >= d.可見五行_佔比_最少;
    checks.push(ok);
    if (ok) reasons.push(`主導佔比${(snap.dominantRatio * 100).toFixed(0)}%`);
  }

  if (d.從旺) {
    const ok =
      snap.flags.includes('cong_wang_structural') ||
      snap.flags.includes('element_abundant');
    checks.push(ok);
    if (ok) reasons.push('結構從旺旗');
  }

  if (d.從弱) {
    const ok = snap.flags.includes('cong_ruo_structural');
    checks.push(ok);
    if (ok) reasons.push('結構從弱旗');
  }

  if (d.從兒) {
    const ok = snap.flags.includes('cong_er_structural');
    checks.push(ok);
    if (ok) {
      reasons.push(`食傷可見${snap.shiShangCount}·日主${snap.dayMasterCount}`);
    }
  }

  if (d.從財) {
    const ok = snap.flags.includes('cong_cai_structural');
    checks.push(ok);
    if (ok) {
      reasons.push(`財星可見${snap.caiCount}·日主${snap.dayMasterCount}`);
    }
  }

  if (d.從殺) {
    const ok = snap.flags.includes('cong_sha_structural');
    checks.push(ok);
    if (ok) {
      reasons.push(`官殺可見${snap.guanShaCount}·日主${snap.dayMasterCount}`);
    }
  }

  if (d.flags?.length) {
    const needed = d.flags.filter(
      (f) => f !== 'daymaster_weak_placeholder' && f !== 'jingang_placeholder',
    );
    if (needed.length) {
      const ok = needed.every((f) => snap.flags.includes(f));
      checks.push(ok);
      if (ok) {
        const labels = needed.map((f) => FLAG_LABELS[f]).filter((x): x is string => !!x);
        reasons.push(labels.length ? `結構旗：${labels.join('、')}` : '結構旗相符');
      }
    }
  }

  if (!checks.length) return null;

  const mode = d.match_mode ?? 'all';
  const pass = mode === 'any' ? checks.some(Boolean) : checks.every(Boolean);
  if (!pass || !reasons.length) return null;

  const priority = d.priority ?? 50;
  const score = priority + reasons.length;
  const candidateOnly =
    !!d.candidate_only ||
    rule.kind === 'cong' ||
    rule.kind === 'tiaohe' ||
    rule.kind === 'placeholder';

  return {
    rule,
    status: candidateOnly ? 'candidate' : 'matched',
    score,
    reasons,
  };
}

/**
 * 對一盤八字跑規則表。
 * - 月令格：結構命中 → matched（仍帶 source_note 待核對）
 * - 從格／調候／佔位：一律 candidate
 * - 多個 yueling 同分 → 全部列入 primary，UI 標 ambiguous
 */
export function matchGeju(r: BaziResult, t: GejuTable = table): GejuMatchResult {
  const structural = structuralSnapshot(r);
  const matches: GejuMatch[] = [];

  for (const rule of t.rules) {
    const m = matchRule(rule, structural);
    if (m) matches.push(m);
  }

  // 若有具體 金旺／從旺，收起通用 cong_wang_generic
  const hasSpecificCong = matches.some(
    (m) => m.rule.kind === 'cong' && m.rule.id !== 'cong_wang_generic' && m.rule.id.startsWith('cong_wang'),
  );
  // 若有具體從兒／從財／從殺，收起通用從弱（仍保留具體從格）
  const hasSpecificCongFollow = matches.some((m) =>
    ['cong_er', 'cong_cai', 'cong_sha'].includes(m.rule.id),
  );
  let filtered = hasSpecificCong
    ? matches.filter((m) => m.rule.id !== 'cong_wang_generic')
    : matches;
  if (hasSpecificCongFollow) {
    filtered = filtered.filter((m) => m.rule.id !== 'cong_ruo_generic');
  }

  filtered.sort((a, b) => b.score - a.score);

  const yue = filtered.filter((m) => m.rule.kind === 'yueling' || !m.rule.kind);
  const topScore = yue[0]?.score ?? -1;
  const topYue = yue.filter((m) => m.score === topScore);

  let primary: GejuMatch[];
  if (topYue.length === 1) {
    primary = [topYue[0]];
  } else if (topYue.length > 1) {
    primary = topYue.map((m) => ({ ...m, status: 'ambiguous' as MatchStatus }));
  } else {
    // 無月令格 → 以最高分候選作 primary
    const top = filtered[0]?.score ?? -1;
    primary = filtered.filter((m) => m.score === top).map((m) => ({
      ...m,
      status: 'candidate' as MatchStatus,
    }));
  }

  return { matches: filtered, primary, structural };
}

export { emptyWx };
